import { parse, serialize } from 'cookie';

import { parseJson } from '@cntxt/shared/utilities/errors';

import { adminRouteRequiredAccess } from './routesRequiredAccess';
import { AccessRequired } from './types';

export const API_BASE = '/api/v1';
export const NEXT_PUBLIC_API_INTERNAL_BASE = '/api/internal';
export const API_GRAPHQL_BASE = '/api/graphql';
export const API_INTEGRATION_BASE = '/api/integration';
export const APIS_BASE = [
  API_BASE,
  NEXT_PUBLIC_API_INTERNAL_BASE,
  API_GRAPHQL_BASE,
];

const PERMIT_BASE = '/permits';
const ADMIN_BASE = '/admin';
const AUTH_BASE = '/auth';
const MODULES_BASE = '/modules';
const DASHBOARDS_BASE = '/dashboards';
const MONITORING_BASE = '/monitoring';
const MODULE_ITEM_BASE = `${MODULES_BASE}/:id/items/:itemId`;

export const enum Routes {
  HOME = '',
  API = 'API',
  API_ACCESS = 'API_ACCESS',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  ACCESS_DENIED = 'ACCESS_DENIED',
  AUTH_SIGNIN_ERROR = 'AUTH_SIGNIN_ERROR',
  ACCOUNT_NOT_SETUP = 'ACCOUNT_NOT_SETUP',
  ACCOUNT_NO_ACCESS = 'ACCOUNT_NO_ACCESS',
  DASHBOARD = 'DASHBOARD',
  WORKFLOW_EDIT = 'WORKFLOW_EDIT',
  NOTIFICATIONS = 'NOTIFICATIONS',
  USER_PROFILE = 'USER_PROFILE',
  ADMIN = 'ADMIN',
  ADMIN_GLOBAL_USERS = 'ADMIN_GLOBAL_USERS',
  ADMIN_GLOBAL_USERS_UNASSIGNED = 'ADMIN_GLOBAL_USERS_UNASSIGNED',
  ADMIN_GLOBAL_USER_EDIT = 'ADMIN_GLOBAL_USER_EDIT',
  ADMIN_ORGANIZATION_EDIT = 'ADMIN_ORGANIZATION_EDIT',
  ADMIN_TENANTS = 'ADMIN_TENANTS',
  ADMIN_TENANTS_EDIT = 'ADMIN_TENANTS_EDIT',
  ADMIN_TENANTS_CREATE = 'ADMIN_TENANTS_CREATE',
  ADMIN_WORKFLOW_TYPES = 'ADMIN_WORKFLOW_TYPES',
  ADMIN_WORKFLOW_TYPES_EDIT = 'ADMIN_WORKFLOW_TYPES_EDIT',
  ADMIN_GLOBAL_ORGANIZATIONS = 'ADMIN_GLOBAL_ORGANIZATIONS',
  ADMIN_GLOBAL_ROLES = 'ADMIN_GLOBAL_ROLES',
  ADMIN_CREATE_USER = 'ADMIN_CREATE_USER',
  ADMIN_EDIT_USER = 'ADMIN_EDIT_USER',
  ADMIN_ORG_MANAGE_USERS = 'ADMIN_ORG_MANAGE_USERS',
  ADMIN_USERS = 'ADMIN_USERS',
  ADMIN_USERS_ASSIGN = 'ADMIN_USERS_ASSIGN',
  ADMIN_ORGANIZATIONS = 'ADMIN_ORGANIZATIONS',
  ADMIN_ORGANIZATIONS_WORKFLOWS = 'ADMIN_ORGANIZATIONS_WORKFLOWS',
  ADMIN_ORGANIZATIONS_EDIT = 'ADMIN_ORGANIZATIONS_EDIT',
  ADMIN_ORGANIZATIONS_CREATE = 'ADMIN_ORGANIZATIONS_CREATE',
  ADMIN_ROLES = 'ADMIN_ROLES',
  ADMIN_ROLE_EDIT = 'ADMIN_ROLE_EDIT',
  ADMIN_ROLE_USERS = 'ADMIN_ROLE_USERS',
  ADMIN_ROLE_CREATE = 'ADMIN_ROLE_CREATE',
  ADMIN_DOCS = 'ADMIN_DOCS',
  ADMIN_DOC = 'ADMIN_DOC',
  ADMIN_LOCATIONS = 'ADMIN_LOCATIONS',
  ADMIN_LOCATIONS_CREATE = 'ADMIN_LOCATIONS_CREATE',
  ADMIN_LOCATION_REGIONS = 'ADMIN_LOCATION_REGIONS',
  ADMIN_MODULES = 'ADMIN_MODULES',
  ADMIN_MODULES_WORKFLOWS = 'ADMIN_MODULES_WORKFLOWS',
  ADMIN_AI_MODULE_FILES = 'ADMIN_AI_MODULE_FILES',
  ADMIN_RELEASE_NOTES = 'ADMIN_RELEASE_NOTES',
  ADMIN_RELEASE_NOTES_CREATE = 'ADMIN_RELEASE_NOTES_CREATE',
  ADMIN_RELEASE_NOTES_EDIT = 'ADMIN_RELEASE_NOTES_EDIT',
  ADMIN_RELEASE_NOTES_VIEW = 'ADMIN_RELEASE_NOTES_VIEW',
  ADMIN_DIAGNOSTICS = 'ADMIN_DIAGNOSTICS',
  ADMIN_DIAGNOSTICS_WORKFLOWS = 'ADMIN_DIAGNOSTICS_WORKFLOWS',
  ADMIN_IMPORT = 'ADMIN_IMPORT',
  ADMIN_IMPORT_USER_ROLES = 'ADMIN_IMPORT_USER_ROLES',
  ADMIN_AUDIT_EVENTS = 'ADMIN_AUDIT_EVENTS',
  ADMIN_AUDIT_EVENT = 'ADMIN_AUDIT_EVENT',
  ADMIN_GLOBAL_WORKFLOWS = 'ADMIN_GLOBAL_WORKFLOWS',
  ADMIN_GLOBAL_WORKFLOW = 'ADMIN_GLOBAL_WORKFLOW',
  ADMIN_GLOBAL_WORKFLOW_INSTANCE = 'ADMIN_GLOBAL_WORKFLOW_INSTANCE',
  ADMIN_GLOBAL_WORKFLOW_BUILDER = 'ADMIN_GLOBAL_WORKFLOW_BUILDER',
  ADMIN_GLOBAL_WORKFLOW_BUILDER_SESSION = 'ADMIN_GLOBAL_WORKFLOW_BUILDER_SESSION',
  PAGE_ERROR = 'PAGE_ERROR',
  PAGE_ERROR_KNOWN = 'PAGE_ERROR_KNOWN',
  PERMIT_MONITORING = 'PERMIT_MONITORING',
  MONITORING = 'MONITORING',
  MONITORING_VIEW = 'MONITORING_VIEW',
  MODULE_DASHBOARD = 'MODULE_DASHBOARD',
  MODULES = 'MODULES',
  MODULE_ITEM = 'MODULE_ITEM',
  MODULE_ITEM_PRINT = 'MODULE_ITEM_PRINT',
  MODULE_ITEM_HISTORY = 'MODULE_ITEM_HISTORY',
  DASHBOARDS = 'DASHBOARDS',
  DASHBOARD_VIEW = 'DASHBOARD_VIEW',
  RELEASE_NOTES = 'RELEASE_NOTES',
  AI_DOCS = 'AI_DOCS',
}

export type Route = {
  url: string;
  public?: boolean;
  data?: {
    /**
     * @description this is used to store session/cookie information
     */
    storageKey?: string;
    /**
     * @description this is used to store the name of the query param key. Should be present in url
     */
    nameQueryParamKey?: string;
  };
};

export const routes = {
  [Routes.HOME]: {
    url: '/',
    public: true,
  },
  [Routes.API]: {
    url: API_BASE,
    public: true,
  },
  [Routes.API_ACCESS]: {
    url: `${API_BASE}/iam/access`,
    public: true,
  },
  [Routes.LOGIN]: {
    url: `${AUTH_BASE}/signIn`,
    public: true,
  },
  [Routes.LOGOUT]: {
    url: `${AUTH_BASE}/signOut`,
    public: true,
  },
  [Routes.ACCESS_DENIED]: {
    url: `${AUTH_BASE}/error/denied`,
    public: true,
    data: {
      storageKey: 'accessDenied-data',
    },
  },
  [Routes.AUTH_SIGNIN_ERROR]: {
    url: `${AUTH_BASE}/error/signin`,
    public: true,
  },
  [Routes.ACCOUNT_NOT_SETUP]: {
    url: `${AUTH_BASE}/error/account?type=notSetup`,
    public: true,
  },
  [Routes.ACCOUNT_NO_ACCESS]: {
    url: `${AUTH_BASE}/error/account?type=noSubscription`,
    public: true,
  },
  [Routes.DASHBOARD]: {
    url: '/dashboard',
  },
  [Routes.WORKFLOW_EDIT]: {
    url: `${PERMIT_BASE}/:id`,
  },
  [Routes.NOTIFICATIONS]: {
    url: '/notifications',
  },
  [Routes.USER_PROFILE]: {
    url: '/user',
  },
  [Routes.ADMIN]: {
    url: `${ADMIN_BASE}/dashboard`,
  },
  [Routes.ADMIN_GLOBAL_USERS]: {
    url: `${ADMIN_BASE}/global/users`,
  },
  [Routes.ADMIN_GLOBAL_USERS_UNASSIGNED]: {
    url: `${ADMIN_BASE}/global/unassigned`,
  },
  [Routes.ADMIN_GLOBAL_USER_EDIT]: {
    url: `${ADMIN_BASE}/global/users/:id`,
  },
  [Routes.ADMIN_ORGANIZATION_EDIT]: {
    public: true,
    url: `${ADMIN_BASE}/global/orgs/:id?name=:name`,
    data: {
      nameQueryParamKey: 'name',
    },
  },
  [Routes.ADMIN_GLOBAL_ORGANIZATIONS]: {
    url: `${ADMIN_BASE}/global/orgs`,
  },
  [Routes.ADMIN_TENANTS]: {
    url: `${ADMIN_BASE}/global/tenants`,
  },
  [Routes.ADMIN_TENANTS_EDIT]: {
    public: true,
    url: `${ADMIN_BASE}/global/tenants/:id?name=:name`,
    data: {
      nameQueryParamKey: 'name',
    },
  },
  [Routes.ADMIN_TENANTS_CREATE]: {
    url: `${ADMIN_BASE}/global/tenants/create`,
  },
  [Routes.ADMIN_GLOBAL_ROLES]: {
    url: `${ADMIN_BASE}/global/roles`,
  },
  [Routes.ADMIN_CREATE_USER]: {
    url: `${ADMIN_BASE}/users/create`,
  },
  [Routes.ADMIN_EDIT_USER]: {
    url: `${ADMIN_BASE}/users/:id`,
  },
  [Routes.ADMIN_ORG_MANAGE_USERS]: {
    url: `${ADMIN_BASE}/orgs/:id/users`,
  },
  [Routes.ADMIN_USERS]: {
    url: `${ADMIN_BASE}/users`,
  },
  [Routes.ADMIN_USERS_ASSIGN]: {
    url: `${ADMIN_BASE}/orgs/:id/users/assign`,
  },
  [Routes.ADMIN_ORGANIZATIONS]: {
    url: `${ADMIN_BASE}/orgs`,
  },
  [Routes.ADMIN_ORGANIZATIONS_WORKFLOWS]: {
    url: `${ADMIN_BASE}/orgs/:id/:moduleId/workflows`,
  },
  [Routes.ADMIN_ORGANIZATIONS_EDIT]: {
    url: `${ADMIN_BASE}/orgs/:id`,
  },
  [Routes.ADMIN_ORGANIZATIONS_CREATE]: {
    url: `${ADMIN_BASE}/orgs/create`,
  },
  [Routes.ADMIN_ROLES]: {
    url: `${ADMIN_BASE}/roles`,
  },
  [Routes.ADMIN_ROLE_EDIT]: {
    url: `${ADMIN_BASE}/roles/:id`,
  },
  [Routes.ADMIN_ROLE_USERS]: {
    url: `${ADMIN_BASE}/roles/:id/users`,
  },
  [Routes.ADMIN_ROLE_CREATE]: {
    url: `${ADMIN_BASE}/roles/create`,
  },
  [Routes.ADMIN_WORKFLOW_TYPES]: {
    url: `${ADMIN_BASE}/workflowType`,
  },
  [Routes.ADMIN_WORKFLOW_TYPES_EDIT]: {
    url: `${ADMIN_BASE}/workflowType/:id`,
  },
  [Routes.ADMIN_DOCS]: {
    url: `${ADMIN_BASE}/docs`,
  },
  [Routes.ADMIN_DOC]: {
    url: `${ADMIN_BASE}/docs/:id`,
  },
  [Routes.ADMIN_LOCATIONS]: {
    url: `${ADMIN_BASE}/locations`,
  },
  [Routes.ADMIN_LOCATIONS_CREATE]: {
    url: `${ADMIN_BASE}/locations/create`,
  },
  [Routes.ADMIN_LOCATION_REGIONS]: {
    url: `${ADMIN_BASE}/locations/:id/regions`,
  },
  [Routes.ADMIN_MODULES]: {
    url: `${ADMIN_BASE}/modules`,
  },
  [Routes.ADMIN_MODULES_WORKFLOWS]: {
    url: `${ADMIN_BASE}/modules/:moduleId/workflows`,
  },
  [Routes.ADMIN_AI_MODULE_FILES]: {
    url: `${ADMIN_BASE}/module-files`,
  },
  [Routes.ADMIN_RELEASE_NOTES]: {
    url: `${ADMIN_BASE}/release-notes`,
  },
  [Routes.ADMIN_RELEASE_NOTES_CREATE]: {
    url: `${ADMIN_BASE}/release-notes/create`,
  },
  [Routes.ADMIN_RELEASE_NOTES_EDIT]: {
    url: `${ADMIN_BASE}/release-notes/:id`,
  },
  [Routes.ADMIN_DIAGNOSTICS]: {
    url: `${ADMIN_BASE}/global/diagnostics`,
  },
  [Routes.ADMIN_DIAGNOSTICS_WORKFLOWS]: {
    url: `${ADMIN_BASE}/global/diagnostics/workflows`,
  },
  [Routes.ADMIN_IMPORT]: {
    url: `${ADMIN_BASE}/global/import`,
  },
  [Routes.ADMIN_IMPORT_USER_ROLES]: {
    url: `${ADMIN_BASE}/global/import/user-roles`,
  },
  // Named generically rather than `temporal-workflows`: which engine runs a
  // definition is an implementation detail that should not be baked into a URL,
  // and BPMN is on the way out. A workflow is addressed by its opaque id alone:
  // tenant, org and module all hang off that row, so a full path would
  // duplicate state that can drift and make a pasted link ambiguous.
  [Routes.ADMIN_GLOBAL_WORKFLOWS]: {
    url: `${ADMIN_BASE}/global/workflows`,
  },
  [Routes.ADMIN_GLOBAL_WORKFLOW]: {
    url: `${ADMIN_BASE}/global/workflows/:workflowId`,
  },
  // Redirect only, no UI. It exists so an id copied out of Temporal's Web UI is
  // directly pasteable: instance mode is a query param on the workflow above,
  // and that needs a workflow id which the holder of an instance id does not
  // have. The static `instances` segment takes precedence over `:workflowId` in
  // Next's routing, which is the desired behaviour.
  [Routes.ADMIN_GLOBAL_WORKFLOW_INSTANCE]: {
    url: `${ADMIN_BASE}/global/workflows/instances/:instanceId`,
  },
  // The Workflow Copilot: sessions that build a definition by conversation,
  // rather than reading one already uploaded. A sibling of the estate above and
  // not a mode of it, because a session exists before any workflow does.
  [Routes.ADMIN_GLOBAL_WORKFLOW_BUILDER]: {
    url: `${ADMIN_BASE}/global/workflow-builder`,
  },
  [Routes.ADMIN_GLOBAL_WORKFLOW_BUILDER_SESSION]: {
    url: `${ADMIN_BASE}/global/workflow-builder/:sessionId`,
  },
  [Routes.ADMIN_AUDIT_EVENTS]: {
    url: `${ADMIN_BASE}/audit-events`,
  },
  [Routes.ADMIN_AUDIT_EVENT]: {
    url: `${ADMIN_BASE}/audit-events/:id`,
  },
  [Routes.ADMIN_RELEASE_NOTES_VIEW]: {
    url: `${ADMIN_BASE}/release-notes/:id/view`,
  },
  [Routes.PAGE_ERROR]: {
    url: '/error',
    public: true,
  },
  [Routes.PAGE_ERROR_KNOWN]: {
    url: '/error/:id',
    public: true,
  },
  [Routes.PERMIT_MONITORING]: {
    url: '/monitoring',
  },
  [Routes.MONITORING]: {
    url: `${MONITORING_BASE}`,
  },
  [Routes.MONITORING_VIEW]: {
    url: `${MONITORING_BASE}/:id`,
  },
  [Routes.MODULE_DASHBOARD]: {
    url: `${MODULES_BASE}/:id`,
  },
  [Routes.MODULES]: {
    url: `${MODULES_BASE}`,
  },
  [Routes.MODULE_ITEM]: {
    url: `${MODULE_ITEM_BASE}`,
  },
  [Routes.MODULE_ITEM_PRINT]: {
    url: `${MODULE_ITEM_BASE}/print`,
  },
  [Routes.MODULE_ITEM_HISTORY]: {
    url: `${MODULE_ITEM_BASE}/history`,
  },
  [Routes.DASHBOARDS]: {
    url: `${DASHBOARDS_BASE}`,
  },
  [Routes.DASHBOARD_VIEW]: {
    url: `${DASHBOARDS_BASE}/:id`,
  },
  [Routes.RELEASE_NOTES]: {
    url: '/release-notes',
  },
  [Routes.AI_DOCS]: {
    url: '/ai-docs',
  },
} as const satisfies Readonly<Record<Partial<Routes>, Route>>;

const mapAccessToRoute = (
  accessArray: AccessRequired | undefined,
  route: Routes,
): Record<string, string> => {
  return (
    accessArray?.access?.reduce(
      (acc, action) => {
        acc[action.AccessAction[0]] = routes[route].url;
        return acc;
      },
      {} as Record<string, string>,
    ) || {}
  );
};

/**
 * @description the key should be the access action defined in access.ts file of the route in the value of the map
 */
export const roleDefaultRouteMap: Record<string, string> = {
  ...mapAccessToRoute(adminRouteRequiredAccess, Routes.ADMIN),
};

type ExtractRouteParams<T extends string> =
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends `${infer Before}:${infer Param}/${infer After}`
    ? { [K in Param]: string } & ExtractRouteParams<After>
    : //eslint-disable-next-line @typescript-eslint/no-unused-vars
      T extends `${infer Before}:${infer Param}`
      ? { [K in Param]: string }
      : //eslint-disable-next-line @typescript-eslint/no-empty-object-type
        {};

export type RouteParams = {
  [K in keyof typeof routes]: ExtractRouteParams<(typeof routes)[K]['url']>;
};

export type RouteValues = (typeof routes)[keyof typeof routes]['url'];

export type AccessDeniedData = {
  requestedPath: string;
  requiredAccess?: AccessRequired | AccessRequired[];
  reason?: string;
};

export type RouteData = AccessDeniedData;

export const storeRouteData = (routeName: Routes, data: RouteData) => {
  const storageKey = (routes[routeName] as Route)?.data?.storageKey;
  const dataString = JSON.stringify(data);

  if (storageKey) {
    document.cookie = serialize(storageKey, dataString, { path: '/' });
  }
};

export const retrieveRouteData = <T = RouteData>(
  routeName: Routes,
): T | undefined => {
  const storageKey = (routes[routeName] as Route)?.data?.storageKey;

  if (storageKey) {
    const cookies = parse(document.cookie);
    const cookieValue = cookies[storageKey];
    const accessDeniedData = parseJson(cookieValue || '{}');
    return accessDeniedData as T;
  }

  return undefined;
};
